import { useLocation } from 'react-router-dom'
import { useAppContext } from '../../context/AppContext'
import SkeletonChatList from '../chat/skeleton/SkeletonChatList'
import SkeletonConversation from '../chat/skeleton/SkeletonConversation'
import ClassroomTrendSkeleton from '../classroom/skeleton/ClassroomTrendSkeleton'
import ClassroomSkeletonList from '../classroom/skeleton/ClassroomSkeletonList'
import AnalyticsHeaderSkeleton from '../analytics/skeleton/AnalyticsHeaderSkeleton'
import AnalyticsDataLeftSkeleton from '../analytics/skeleton/AnalyticsDataLeftSkeleton'
import AnalyticsDataRightSkeleton from '../analytics/skeleton/AnalyticsDataRightSkeleton'
import SkeletonManagementHeader from '../management/skeleton/SkeletonManagementHeader'
import SkeletonManagementNavBar from '../management/skeleton/SkeletonManagementNavBar'
import SkeletonAlertsHeader from '../alerts/skeleton/SkeletonAlertsHeader'
import SkeletonAllAlerts from '../alerts/skeleton/SkeletonAllAlerts'
import BannerSkeleton from '../dashboard/skeleton/BannerSkeleton'
import ButtonSkeleton from '../dashboard/skeleton/ButtonsSkeleton'
import YourClassRoomSkeleton from '../dashboard/contents/skeleton/YourClassRoomSkeleton'
import MyTaskWidgetSkeleton from '../dashboard/skeleton/MyTaskWidgetSkeleton'
import EarlyDashboardSkeleton from '../early/dashboard/skeleton/DashboardSkeleton'
import CounsellingLeftSideSkeleton from '../counseling/skeleton/CounsellingLeftSideSkeleton'
import { WellReminderHeaderSkeleton, TypeOptionsListsSkeleton } from '../counseling/skeleton/CounsellingHomeSkeleton'
import '../../styles/routeSkeleton.css'

// These are static imports of presentation-only placeholders. Every fallback
// is available before a destination's lazy route starts downloading; no page
// component, widget implementation, or backend data is imported here.
const matches = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`)

function PlaceholderCards() {
    return <div className="route-skeleton__cards animate-pulse">
        {Array.from({ length: 4 }, (_, index) => <div key={index} className="route-skeleton__card">
            <div className="h-6 w-1/3 rounded bg-lightBG mb-6" />
            <div className="h-4 w-full rounded bg-lightBG mb-4" />
            <div className="h-4 w-2/3 rounded bg-lightBG" />
        </div>)}
    </div>
}

export default function RouteSkeleton() {
    const { pathname, search } = useLocation()
    const { appState } = useAppContext()
    let kind = 'page'
    let content = <><AnalyticsHeaderSkeleton /><PlaceholderCards /></>

    if (matches(pathname, '/chat')) {
        kind = 'chat'
        content = <div className="chat-home-container">
            <div className="chat-container">
                <SkeletonChatList />
                {new URLSearchParams(search).has('conversationId') && <SkeletonConversation />}
            </div>
        </div>
    } else if (matches(pathname, '/early/dashboard') || (matches(pathname, '/dashboard') && appState?.primary)) {
        kind = 'early-dashboard'
        content = <EarlyDashboardSkeleton />
    } else if (matches(pathname, '/dashboard')) {
        kind = 'dashboard'
        content = <div className="route-skeleton__columns">
            <div className="route-skeleton__column">
                <BannerSkeleton />
                <ButtonSkeleton />
                <YourClassRoomSkeleton />
            </div>
            <div className="route-skeleton__dashboard-tasks"><MyTaskWidgetSkeleton /></div>
        </div>
    } else if (matches(pathname, '/classroom')) {
        kind = 'classroom'
        content = <><ClassroomTrendSkeleton /><ClassroomSkeletonList /></>
    } else if (matches(pathname, '/analytics')) {
        kind = 'analytics'
        content = <>
            <AnalyticsHeaderSkeleton />
            <div className="route-skeleton__columns"><AnalyticsDataLeftSkeleton /><AnalyticsDataRightSkeleton /></div>
        </>
    } else if (matches(pathname, '/management')) {
        kind = 'management'
        content = <><SkeletonManagementHeader /><SkeletonManagementNavBar /><PlaceholderCards /></>
    } else if (pathname === '/counseling') {
        kind = 'counseling'
        content = <div className="well-container bg-[#ECEBF8]">
            <CounsellingLeftSideSkeleton />
            <div className="well-reminder-container"><WellReminderHeaderSkeleton /><TypeOptionsListsSkeleton /></div>
        </div>
    } else if (matches(pathname, '/alerts')) {
        kind = 'alerts'
        content = <><SkeletonAlertsHeader /><SkeletonAllAlerts /></>
    }

    return <div data-route-loading="true" data-route-skeleton={kind} className="route-skeleton" role="status" aria-label="Loading page" aria-busy="true">
        <div aria-hidden="true" className={kind === 'chat' || kind === 'counseling' ? 'route-skeleton__body' : 'route-skeleton__page'}>
            {content}
        </div>
    </div>
}
